import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/budgets`;

interface BudgetLine {
  id: number;
  period: string;
  gl_account: string | null;
  cost_center: string | null;
  department: string | null;
  budget_amount: number;
  revised_amount: number | null;
  effective_amount: number;
  description: string | null;
}

interface Budget {
  id: number;
  budget_name: string;
  budget_type: string;
  entity_code: string | null;
  fiscal_year: number;
  period_type: string;
  currency: string;
  status: string;
  total_amount: number;
  line_count: number;
  version: number;
  lines?: BudgetLine[];
}

interface BudgetCheck {
  has_budget: boolean;
  budget_amount: number;
  ytd_actuals: number;
  committed: number;
  this_transaction: number;
  remaining: number;
  remaining_after: number;
  utilization_pct: number;
  warning: string | null;
  status: string;
}

const CURRENT_YEAR = new Date().getFullYear();

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 14px', color: 'var(--text-hint)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px' };

export default function BudgetManagement() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('budgets');
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedBudget, setExpandedBudget] = useState<Budget | null>(null);
  const [yearFilter, setYearFilter] = useState<number>(CURRENT_YEAR);
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Create form
  const [form, setForm] = useState({
    budget_name: '', budget_type: 'OPEX', entity_code: '', fiscal_year: String(CURRENT_YEAR),
    period_type: 'QUARTERLY', currency: 'USD', notes: '',
    lines: [{ period: `${CURRENT_YEAR}-Q1`, gl_account: '', cost_center: '', department: '', budget_amount: '', description: '' }],
  });

  // Budget check form
  const [checkForm, setCheckForm] = useState({ entity_code: '', gl_account: '', cost_center: '', department: '', amount: '', period: '' });
  const [checkResult, setCheckResult] = useState<BudgetCheck | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (yearFilter) p.set('fiscal_year', String(yearFilter));
      if (statusFilter) p.set('status', statusFilter);
      const res = await authFetch(withEntityParam(`${API}?${p}`, entity));
      if (res.ok) setBudgets(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [yearFilter, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setExpandedBudget(null); return; }
    try {
      const res = await authFetch(`${API}/${id}`);
      if (res.ok) { const d = await res.json(); setExpandedBudget(d); setExpandedId(id); }
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        fiscal_year: parseInt(form.fiscal_year),
        lines: form.lines.filter(l => l.budget_amount).map(l => ({
          ...l, budget_amount: parseFloat(l.budget_amount) || 0,
        })),
      };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        showToast('Budget created', true); setTab('budgets'); load();
        setForm({ budget_name: '', budget_type: 'OPEX', entity_code: '', fiscal_year: String(CURRENT_YEAR), period_type: 'QUARTERLY', currency: 'USD', notes: '', lines: [{ period: `${CURRENT_YEAR}-Q1`, gl_account: '', cost_center: '', department: '', budget_amount: '', description: '' }] });
      } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed to create budget', false); }
  };

  const handleApprove = async (id: number) => {
    try {
      const res = await authFetch(`${API}/${id}/approve`, { method: 'POST' });
      if (res.ok) { showToast('Budget approved', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Approval failed', false); }
  };

  const handleCheck = async () => {
    setCheckLoading(true); setCheckResult(null);
    try {
      const payload = {
        entity_code: checkForm.entity_code || 'USA',
        gl_account: checkForm.gl_account || undefined,
        cost_center: checkForm.cost_center || undefined,
        department: checkForm.department || undefined,
        amount: parseFloat(checkForm.amount) || 0,
        period: checkForm.period || undefined,
      };
      const res = await authFetch(`${API}/check`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) setCheckResult(await res.json());
      else { const e = await res.json(); showToast(e.detail || 'Check failed', false); }
    } catch { showToast('Budget check failed', false); }
    setCheckLoading(false);
  };

  const addLine = () => setForm({
    ...form,
    lines: [...form.lines, { period: '', gl_account: '', cost_center: '', department: '', budget_amount: '', description: '' }],
  });
  const updateLine = (i: number, field: string, value: string) => {
    const lines = [...form.lines]; (lines[i] as any)[field] = value; setForm({ ...form, lines });
  };
  const removeLine = (i: number) => { const lines = form.lines.filter((_, idx) => idx !== i); setForm({ ...form, lines }); };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  // KPIs
  const totalBudgets = budgets.length;
  const activeBudgets = budgets.filter(b => b.status === 'ACTIVE').length;
  const draftBudgets = budgets.filter(b => b.status === 'DRAFT').length;
  const totalAmount = budgets.filter(b => b.status === 'ACTIVE').reduce((s, b) => s + b.total_amount, 0);

  return (
    <PageLayout
      title="Budget & Planning"
      subtitle="Create budgets, track utilization, and check availability before transactions"
    >
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: toast.ok ? '#22c55e' : '#ef4444', color: '#fff' }}>
          {toast.msg}
        </div>
      )}

      <KpiGrid items={[
        { label: 'Total Budgets', value: String(totalBudgets) },
        { label: 'Active', value: String(activeBudgets), color: '#22c55e' },
        { label: 'Draft', value: String(draftBudgets), color: 'var(--text-secondary)' },
        { label: 'Active Budget Total', value: formatAmountCompact(totalAmount), color: 'var(--accent)' },
      ]} columns={4} />

      <TabBar
        tabs={[
          { key: 'budgets', label: 'Budgets' },
          { key: 'check', label: 'Budget Check' },
          { key: 'create', label: 'Create Budget' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* BUDGETS TAB */}
      {tab === 'budgets' && (
        <div>
          <FilterBar filters={[
            {
              key: 'year', label: 'Year', type: 'select', value: String(yearFilter),
              onChange: v => setYearFilter(Number(v)), width: 120,
              options: [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map(y => ({ value: String(y), label: String(y) })),
            },
            {
              key: 'status', label: 'Status', type: 'select', value: statusFilter,
              onChange: setStatusFilter, width: 140,
              options: [
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'CLOSED', label: 'Closed' },
              ],
            },
          ]} />

          {loading ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div> : (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Budget Name</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Type</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Entity</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Year</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Period</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Lines</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>No budgets found. Create one to get started.</td></tr>
                  ) : budgets.map(b => (
                    <>
                      <tr key={b.id} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }} onClick={() => loadDetail(b.id)}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{b.budget_name}</td>
                        <td style={tdStyle}>{b.budget_type}</td>
                        <td style={tdStyle}>{b.entity_code || 'All'}</td>
                        <td style={tdStyle}>{b.fiscal_year}</td>
                        <td style={tdStyle}>{b.period_type}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(b.total_amount)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{b.line_count}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={b.status} /></td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {b.status === 'DRAFT' && (
                            <ActionButton label="Approve" onClick={e => { e.stopPropagation(); handleApprove(b.id); }} variant="success" size="sm" />
                          )}
                        </td>
                      </tr>
                      {expandedId === b.id && expandedBudget?.lines && (
                        <tr key={`${b.id}-detail`}>
                          <td colSpan={9} style={{ padding: 0 }}>
                            <div style={{ padding: '12px 20px', background: 'var(--bg-hover)' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>BUDGET LINES</div>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Period</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>GL Account</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Cost Center</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Department</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Budget</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Revised</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Effective</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedBudget.lines.map((l, i) => (
                                    <tr key={i} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                      <td style={tdStyle}>{l.period}</td>
                                      <td style={tdStyle}>{l.gl_account || '-'}</td>
                                      <td style={tdStyle}>{l.cost_center || '-'}</td>
                                      <td style={tdStyle}>{l.department || '-'}</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(l.budget_amount)}</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>{l.revised_amount != null ? formatAmount(l.revised_amount) : '-'}</td>
                                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(l.effective_amount)}</td>
                                      <td style={{ ...tdStyle, color: 'var(--text-hint)' }}>{l.description || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* BUDGET CHECK TAB */}
      {tab === 'check' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700 }}>Check Budget Availability</h3>
          <p style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 20 }}>
            Verify if a proposed transaction fits within the approved budget before committing.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Entity Code *</label>
              <input style={inputStyle} value={checkForm.entity_code} onChange={e => setCheckForm({ ...checkForm, entity_code: e.target.value })} placeholder="e.g. USA" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>GL Account</label>
              <input style={inputStyle} value={checkForm.gl_account} onChange={e => setCheckForm({ ...checkForm, gl_account: e.target.value })} placeholder="e.g. 620000" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Cost Center</label>
              <input style={inputStyle} value={checkForm.cost_center} onChange={e => setCheckForm({ ...checkForm, cost_center: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Department</label>
              <input style={inputStyle} value={checkForm.department} onChange={e => setCheckForm({ ...checkForm, department: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Transaction Amount *</label>
              <input style={inputStyle} type="number" value={checkForm.amount} onChange={e => setCheckForm({ ...checkForm, amount: e.target.value })} placeholder="0.00" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Period</label>
              <input style={inputStyle} value={checkForm.period} onChange={e => setCheckForm({ ...checkForm, period: e.target.value })} placeholder={`e.g. ${CURRENT_YEAR}-Q1`} />
            </div>
          </div>
          <ActionButton label={checkLoading ? 'Checking...' : 'Check Budget'} onClick={handleCheck} disabled={checkLoading || !checkForm.entity_code || !checkForm.amount} />

          {checkResult && (
            <div style={{ marginTop: 20, padding: 20, borderRadius: 8, border: `2px solid ${checkResult.status === 'OK' ? '#22c55e' : checkResult.status === 'WARNING' ? '#f59e0b' : checkResult.status === 'OVER_BUDGET' ? '#ef4444' : 'var(--border-subtle)'}`, background: 'var(--bg-hover)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  padding: '4px 14px', borderRadius: 4, fontWeight: 700, fontSize: 12,
                  background: checkResult.status === 'OK' ? '#22c55e' : checkResult.status === 'WARNING' ? '#f59e0b' : checkResult.status === 'OVER_BUDGET' ? '#ef4444' : '#94a3b8',
                  color: '#fff',
                }}>
                  {checkResult.status === 'NO_BUDGET' ? 'NO BUDGET FOUND' : checkResult.status}
                </div>
                {checkResult.warning && <span style={{ fontSize: 12, color: checkResult.status === 'OVER_BUDGET' ? '#ef4444' : '#f59e0b' }}>{checkResult.warning}</span>}
              </div>

              {checkResult.has_budget && (
                <KpiGrid items={[
                  { label: 'Budget Amount', value: formatAmountCompact(checkResult.budget_amount) },
                  { label: 'YTD Actuals', value: formatAmountCompact(checkResult.ytd_actuals) },
                  { label: 'Committed (Open POs)', value: formatAmountCompact(checkResult.committed) },
                  { label: 'Remaining After', value: formatAmountCompact(checkResult.remaining_after), color: checkResult.remaining_after < 0 ? '#ef4444' : '#22c55e' },
                ]} columns={4} />
              )}

              {checkResult.has_budget && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Utilization</div>
                  <div style={{ height: 8, background: 'var(--border-default)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(checkResult.utilization_pct, 100)}%`, height: '100%', borderRadius: 4,
                      background: checkResult.utilization_pct > 90 ? '#ef4444' : checkResult.utilization_pct > 75 ? '#f59e0b' : '#22c55e',
                    }} />
                  </div>
                  <div style={{ fontSize: 11, marginTop: 4, color: 'var(--text-hint)' }}>{checkResult.utilization_pct}% utilized</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CREATE BUDGET TAB */}
      {tab === 'create' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700 }}>Create Budget</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Budget Name *</label>
              <input style={inputStyle} value={form.budget_name} onChange={e => setForm({ ...form, budget_name: e.target.value })} placeholder="e.g. FY2026 OpEx Budget" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type</label>
              <select style={inputStyle} value={form.budget_type} onChange={e => setForm({ ...form, budget_type: e.target.value })}>
                <option value="OPEX">OPEX</option>
                <option value="CAPEX">CAPEX</option>
                <option value="REVENUE">REVENUE</option>
                <option value="HEADCOUNT">HEADCOUNT</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Entity Code</label>
              <input style={inputStyle} value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} placeholder="Optional (blank = all)" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Fiscal Year *</label>
              <input style={inputStyle} type="number" value={form.fiscal_year} onChange={e => setForm({ ...form, fiscal_year: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Period Type</label>
              <select style={inputStyle} value={form.period_type} onChange={e => setForm({ ...form, period_type: e.target.value })}>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Currency</label>
              <select style={inputStyle} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                <option value="USD">USD</option>
                <option value="SAR">SAR</option>
                <option value="BRL">BRL</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea style={{ ...inputStyle, minHeight: 48 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional budget notes" />
          </div>

          {/* Lines */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Budget Lines</div>
              <ActionButton label="+ Add Line" onClick={addLine} size="sm" />
            </div>
            <div style={{ background: 'var(--bg-hover)', borderRadius: 6, padding: 12 }}>
              {form.lines.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 100px 100px 100px 110px 1fr 30px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input style={{ ...inputStyle, fontSize: 11 }} value={l.period} onChange={e => updateLine(i, 'period', e.target.value)} placeholder="Period" />
                  <input style={{ ...inputStyle, fontSize: 11 }} value={l.gl_account} onChange={e => updateLine(i, 'gl_account', e.target.value)} placeholder="GL Acct" />
                  <input style={{ ...inputStyle, fontSize: 11 }} value={l.cost_center} onChange={e => updateLine(i, 'cost_center', e.target.value)} placeholder="Cost Ctr" />
                  <input style={{ ...inputStyle, fontSize: 11 }} value={l.department} onChange={e => updateLine(i, 'department', e.target.value)} placeholder="Dept" />
                  <input style={{ ...inputStyle, fontSize: 11 }} type="number" value={l.budget_amount} onChange={e => updateLine(i, 'budget_amount', e.target.value)} placeholder="Amount" />
                  <input style={{ ...inputStyle, fontSize: 11 }} value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" />
                  {form.lines.length > 1 && (
                    <button onClick={() => removeLine(i)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14 }}>x</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <ActionButton label="Create Budget" onClick={handleCreate} disabled={!form.budget_name || !form.fiscal_year} />
        </div>
      )}
    </PageLayout>
  );
}
